/**
 * custom.js - Espírito Livre • vSAFE ORCHESTRA 01
 *
 * Objetivo desta versão:
 * - Remover interceptadores globais de fetch/XHR.
 * - Parar de forçar reload depois de adicionar ao carrinho.
 * - Interceptar apenas formulários reais de produto.
 * - Preservar checkout, Yampi, Shopify, pixels, captcha e apps externos.
 * - Atualizar contador/carrinho com /cart.js.
 * - Abrir mini-cart de forma limpa quando possível.
 * - Manter sticky buy mobile integrado com Quick Buy sem duplicar ações.
 */

(function () {
  'use strict';

  /* =========================================================
     CONFIGURAÇÕES GERAIS
  ========================================================= */

  const MOBILE_BREAK = 989;

  const SELECTORS = {
    productForm: 'form[action*="/cart/add"], .product-form',
    addToCartBtn: [
  'button[name="add"]',
  '[name="add"]',
  '[data-add-to-cart]',
  '[data-product-add-to-cart-button]',
  'button[data-product-add-to-cart-button]',
  '.add-to-cart',
  'button.add-to-cart',
  '.product-form__submit',
  '.product-form__add-button',
  '.ProductForm__AddToCart',
  '#AddToCart',
  '#add-to-cart'
].join(','),

    productImage: [
      '.product__media img',
      '.product-media img',
      'img[data-media-id]',
      '.product__image img',
      '.product-gallery img',
      '.product__media-item img'
    ].join(','),

    cartCount: [
      '[data-cart-count]',
      '.cart-count',
      '.header-cart-count',
      '.js-cart-count',
      '#cart-icon-bubble .cart-count-bubble',
      '.cart-count-bubble'
    ].join(','),

    miniCart: [
      '#mini-cart',
      '.mini-cart',
      '[data-mini-cart]',
      'cart-drawer',
      '#cart-drawer'
    ].join(','),

    miniCartItems: [
      '[data-cart-items]',
      '.mini-cart__items',
      '.mini-cart-items',
      '.cart-drawer__items',
      '.cart-items'
    ].join(','),

    headerCartIcon: [
      '[aria-controls="mini-cart"]',
      '[data-cart-icon]',
      '.site-header__cart',
      '.header__icons a[href*="/cart"]',
      'a[href="/cart"]',
      '#cart-icon-bubble',
      '.icon-cart'
    ].join(',')
  };

  const API = {
    add: '/cart/add.js',
    cart: '/cart.js'
  };

  let isAdding = false;
  let observerStarted = false;

  /* =========================================================
     HELPERS
  ========================================================= */

  function qs(selector, root = document) {
    return root.querySelector(selector);
  }

  function qsa(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
  }

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAK;
  }

  function safeClosest(target, selector) {
    try {
      return target && target.closest ? target.closest(selector) : null;
    } catch (_) {
      return null;
    }
  }

  function formatPrice(cents) {
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return 'R$ ' + ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
    }
  }

function isQuickBuyOpen() {
  try {
    const sheet = document.getElementById('qbsSheet');
    const overlay = document.getElementById('qbsOverlay');

    const sheetOpen = !!(
      sheet &&
      (
        sheet.classList.contains('open') ||
        sheet.getAttribute('aria-hidden') === 'false'
      )
    );

    const overlayOpen = !!(
      overlay &&
      (
        overlay.classList.contains('open') ||
        overlay.getAttribute('aria-hidden') === 'false'
      )
    );

    return sheetOpen || overlayOpen;
  } catch (_) {
    return false;
  }
}

function isQuickBuyLocked() {
  const locked =
    window.__EL_QUICK_BUY_LOCK === true ||
    window.__EL_ADD_CONTEXT === 'quickbuy' ||
    document.documentElement.hasAttribute('data-qbs-opening');

  if (!locked) return false;

  if (isQuickBuyOpen()) return true;

  try {
    window.__EL_QUICK_BUY_LOCK = false;
    window.__EL_ADD_CONTEXT = null;
    window.__qbsSuppressAdd = false;
    document.documentElement.removeAttribute('data-qbs-opening');
    document.body && document.body.classList.remove('el-cart-open');
  } catch (_) {}

  return false;
}

  function isCheckoutAction(target) {
    try {
      if (!target || !target.closest) return false;

      if (target.closest('a[href*="/checkout"]')) return true;
      if (target.closest('form[action*="/checkout"]')) return true;
      if (target.closest('button[name="checkout"]')) return true;
      if (target.closest('[data-checkout]')) return true;
      if (target.closest('.cart__checkout-button')) return true;
      if (target.closest('.checkout-button')) return true;
      if (target.closest('.shopify-payment-button')) return true;
      if (target.closest('.shopify-payment-button__button')) return true;
      if (target.closest('shopify-accelerated-checkout')) return true;

      return false;
    } catch (_) {
      return false;
    }
  }

  function findCartIcon() {
    let el = qs(SELECTORS.headerCartIcon);

    if (el) return el;

    el = Array.from(document.querySelectorAll('a, button')).find((node) => {
      try {
        const text = [
          node.getAttribute('aria-label') || '',
          node.getAttribute('title') || '',
          node.textContent || ''
        ].join(' ');

        return /carrinho|cart|sacola|bag/i.test(text);
      } catch (_) {
        return false;
      }
    });

    return el || document.body;
  }



function getProductForm(target) {
  try {
    if (!target) return null;

    const searchForm = safeClosest(target, 'form[action*="/search"], form[role="search"], form.search, form.predictive-search, predictive-search form');

    if (searchForm) return null;

    const form =
      safeClosest(target, 'form[action*="/cart/add"]') ||
      safeClosest(target, '.product-form');

    if (!form) return null;

    const formAction = String(form.getAttribute('action') || '').toLowerCase();
    const formRole = String(form.getAttribute('role') || '').toLowerCase();
    const formClass = String(form.className || '').toLowerCase();
    const formId = String(form.id || '').toLowerCase();

    const isSearchForm =
      formAction.includes('/search') ||
      formRole === 'search' ||
      formClass.includes('search') ||
      formClass.includes('predictive') ||
      formId.includes('search') ||
      !!form.querySelector('input[type="search"], input[name="q"], [role="searchbox"]');

    if (isSearchForm) return null;

    const action = String(form.getAttribute('action') || '');

    if (action.includes('/cart/add')) return form;

    if (form.querySelector('input[name="id"], select[name="id"], input[name="variant_id"]')) {
      return form;
    }

    return null;
  } catch (_) {
    return null;
  }
}

  function getSubmitButton(form, event) {
    if (event && event.submitter) return event.submitter;

    return form.querySelector(SELECTORS.addToCartBtn) || form.querySelector('button[type="submit"], input[type="submit"]');
  }

  function hasVariantId(form) {
    const idInput = form.querySelector('input[name="id"], select[name="id"], input[name="variant_id"]');
    return !!(idInput && idInput.value);
  }
function elNormalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function elIsSearchContext(node) {
  try {
    if (!node) return false;

    const el = node.nodeType === 1 ? node : node.parentElement;
    if (!el) return false;

    const tag = String(el.tagName || '').toLowerCase();
    const type = elNormalizeSearchText(el.getAttribute && el.getAttribute('type'));
    const name = elNormalizeSearchText(el.getAttribute && el.getAttribute('name'));
    const role = elNormalizeSearchText(el.getAttribute && el.getAttribute('role'));
    const aria = elNormalizeSearchText(el.getAttribute && el.getAttribute('aria-label'));
    const placeholder = elNormalizeSearchText(el.getAttribute && el.getAttribute('placeholder'));
    const cls = elNormalizeSearchText(el.className);
    const id = elNormalizeSearchText(el.id);

    if (
      tag === 'input' &&
      (
        type === 'search' ||
        name === 'q' ||
        role === 'searchbox' ||
        aria.includes('buscar') ||
        aria.includes('search') ||
        placeholder.includes('buscar') ||
        placeholder.includes('search') ||
        cls.includes('search') ||
        id.includes('search')
      )
    ) {
      return true;
    }

    const searchWrapper = el.closest(
      'form[action*="/search"], ' +
      'form[role="search"], ' +
      'form.search, ' +
      'form.predictive-search, ' +
      'predictive-search, ' +
      '.predictive-search, ' +
      '.search-modal, ' +
      '.search-drawer, ' +
      '.header-search, ' +
      '.search, ' +
      '[role="search"]'
    );

    if (searchWrapper) return true;

    return false;
  } catch (_) {
    return false;
  }
}

function elFindSearchInput(context) {
  try {
    const root =
      context && context.closest
        ? (
            context.closest('form[action*="/search"], form[role="search"], predictive-search, .predictive-search, .search-modal, .search-drawer, .header-search, .search') ||
            context.closest('form') ||
            document
          )
        : document;

    return (
      root.querySelector('input[name="q"]') ||
      root.querySelector('input[type="search"]') ||
      root.querySelector('[role="searchbox"]') ||
      document.querySelector('input[name="q"]:focus') ||
      document.querySelector('input[type="search"]:focus') ||
      document.activeElement
    );
  } catch (_) {
    return document.activeElement;
  }
}

function elSubmitSearchFromContext(context) {
  try {
    const input = elFindSearchInput(context);
    const query = input && input.value ? String(input.value).trim() : '';

    if (!query) return false;

    window.location.href = '/search?q=' + encodeURIComponent(query);
    return true;
  } catch (_) {
    return false;
  }
}

function installSearchSubmitGuard() {
  if (window.__EL_SEARCH_SUBMIT_GUARD_HARD_V2__) return;
  window.__EL_SEARCH_SUBMIT_GUARD_HARD_V2__ = true;

  document.addEventListener('submit', function (event) {
    if (
      !elIsSearchContext(event.target) &&
      !elIsSearchContext(event.submitter) &&
      !elIsSearchContext(document.activeElement)
    ) {
      return;
    }

    try {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    } catch (_) {}

    elSubmitSearchFromContext(document.activeElement || event.target);
    return false;
  }, true);

  document.addEventListener('click', function (event) {
    const target = event.target;

    if (!elIsSearchContext(target)) return;

    const btn = safeClosest(target, 'button[type="submit"], input[type="submit"], button, [role="button"]');

    if (!btn) return;

    try {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    } catch (_) {}

    elSubmitSearchFromContext(btn);
    return false;
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!elIsSearchContext(event.target)) return;

    try {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    } catch (_) {}

    elSubmitSearchFromContext(event.target);
    return false;
  }, true);

  console.log('[Espírito Livre] Search Submit Guard HARD v2 ativo.');
}
  /* =========================================================
     ANIMAÇÕES LEVES
  ========================================================= */

  function flyToCartAnimation(imgEl, cartBtn) {
    return new Promise((resolve) => {
      try {
        if (!imgEl || !cartBtn || !imgEl.getBoundingClientRect) return resolve();

        const rectImg = imgEl.getBoundingClientRect();
        const rectCart = cartBtn.getBoundingClientRect
          ? cartBtn.getBoundingClientRect()
          : { left: window.innerWidth - 48, top: 10, width: 40, height: 40 };

        if (!rectImg.width || !rectImg.height) return resolve();

        const clone = imgEl.cloneNode(true);

        clone.style.position = 'fixed';
        clone.style.left = rectImg.left + 'px';
        clone.style.top = rectImg.top + 'px';
        clone.style.width = rectImg.width + 'px';
        clone.style.height = rectImg.height + 'px';
        clone.style.transition = 'transform .65s cubic-bezier(.2,.85,.2,1), opacity .55s';
        clone.style.zIndex = '2147483646';
        clone.style.borderRadius = '10px';
        clone.style.pointerEvents = 'none';
        clone.style.objectFit = 'cover';

        document.body.appendChild(clone);

        clone.getBoundingClientRect();

        const translateX = rectCart.left + rectCart.width / 2 - (rectImg.left + rectImg.width / 2);
        const translateY = rectCart.top + rectCart.height / 2 - (rectImg.top + rectImg.height / 2);

        requestAnimationFrame(() => {
          clone.style.transform = `translate(${translateX}px, ${translateY}px) scale(.12)`;
          clone.style.opacity = '0';
        });

        setTimeout(() => {
          clone.remove();
          resolve();
        }, 700);
      } catch (_) {
        resolve();
      }
    });
  }

  function showAddedBadge(anchor) {
    try {
      const badge = document.createElement('div');
      badge.className = 'el-added-badge';
      badge.textContent = 'ADICIONADO ✅';

      let left = null;
      let top = null;

      if (anchor && anchor.getBoundingClientRect) {
        const r = anchor.getBoundingClientRect();
        left = r.left + r.width / 2;
        top = r.top;
      }

      if (left !== null && top !== null) {
        badge.style.left = Math.max(12, left - 70) + 'px';
        badge.style.top = Math.max(12, top - 44) + 'px';
      } else {
        badge.style.right = '18px';
        badge.style.bottom = '92px';
      }

      document.body.appendChild(badge);

      requestAnimationFrame(() => {
        badge.classList.add('is-visible');
      });

      setTimeout(() => {
        badge.classList.remove('is-visible');
        setTimeout(() => badge.remove(), 280);
      }, 1200);
    } catch (_) {}
  }

  function launchConfettiLight(cx = window.innerWidth / 2, cy = window.innerHeight / 3) {
    try {
      const root = document.createElement('div');
      root.className = 'el-confetti-root';
      document.body.appendChild(root);

      const colors = ['#39e7ff', '#a0ff5a', '#ff6ad5', '#ffd36a', '#6f7cff'];

      for (let i = 0; i < 18; i++) {
        const p = document.createElement('div');
        p.className = 'el-confetti-piece';
        p.style.left = cx + (Math.random() * 100 - 50) + 'px';
        p.style.top = cy + (Math.random() * 26 - 13) + 'px';
        p.style.background = colors[Math.floor(Math.random() * colors.length)];
        p.style.width = 5 + Math.random() * 7 + 'px';
        p.style.height = p.style.width;
        p.style.borderRadius = Math.random() > 0.5 ? '50%' : '3px';

        root.appendChild(p);

        requestAnimationFrame(() => {
          p.style.transform = `translate3d(${Math.random() * 180 - 90}px, ${260 + Math.random() * 180}px, 0) rotate(${Math.random() * 540}deg)`;
          p.style.opacity = '0';
        });
      }

      setTimeout(() => root.remove(), 1100);
    } catch (_) {}
  }

  /* =========================================================
     CARRINHO
  ========================================================= */

  async function getCart() {
    const res = await fetch(API.cart, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!res.ok) throw new Error('cart_fetch_failed');

    return res.json();
  }

function updateCartCounters(cart) {
  try {
    const count = cart && typeof cart.item_count !== 'undefined'
      ? Number(cart.item_count) || 0
      : 0;

    window.__EL_LAST_CART_COUNT = count;

    const textTargets = new Set();

    [
      '[data-cart-count]',
      '[data-header-cart-count]',
      '[data-js-cart-count]',
      '.cart-count',
      '.header-cart-count',
      '.js-cart-count',
      '.header__cart-count',
      '.site-header__cart-count',
      '.mini-cart-count',
      '#cart-icon-bubble .cart-count-bubble span:not(.visually-hidden)',
      '.cart-count-bubble span:not(.visually-hidden)'
    ].forEach((selector) => {
      qsa(selector).forEach((el) => textTargets.add(el));
    });

    textTargets.forEach((el) => {
      try {
        el.textContent = String(count);
        el.setAttribute('data-count', String(count));
        el.classList.toggle('is-empty', count <= 0);
        el.classList.toggle('is-hidden', count <= 0);
        el.classList.toggle('hidden', count <= 0);
      } catch (_) {}
    });

    qsa('.cart-count-bubble, #cart-icon-bubble, [aria-controls="mini-cart"]').forEach((el) => {
      try {
        el.setAttribute('data-count', String(count));
        el.classList.toggle('is-empty', count <= 0);
        el.classList.toggle('is-hidden', count <= 0);
        el.classList.toggle('hidden', false);
      } catch (_) {}
    });

  } catch (_) {}
}

  function updateMiniCartBasic(cart) {
    try {
      const container = qs(SELECTORS.miniCartItems);

      if (!container || !cart || !Array.isArray(cart.items)) return;

      container.innerHTML = '';

      if (!cart.items.length) {
        container.innerHTML = '<div class="mini-cart__empty">Seu carrinho está vazio.</div>';
        return;
      }

      cart.items.forEach((item) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'mini-cart__item el-mini-cart-item';

        const image = item.image || '';
        const title = item.product_title || item.title || 'Produto';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        const qty = item.quantity || 1;
        const price = formatPrice(item.final_line_price || item.line_price || item.price || 0);

        wrapper.innerHTML = `
          <div class="mini-cart__thumb el-mini-cart-thumb">
            ${image ? `<img src="${image}" alt="${title.replace(/"/g, '&quot;')}" width="64" height="64" loading="lazy">` : ''}
          </div>
          <div class="mini-cart__meta el-mini-cart-meta">
            <div class="mini-cart__title el-mini-cart-title">${title}</div>
            ${variant ? `<div class="mini-cart__variant el-mini-cart-variant">${variant}</div>` : ''}
            <div class="mini-cart__qty el-mini-cart-qty">Qtd: ${qty}</div>
            <div class="mini-cart__price el-mini-cart-price">${price}</div>
          </div>
        `;

        container.appendChild(wrapper);
      });
    } catch (_) {}
  }

function elIsMiniCartOpen() {
  try {
    const drawer =
      document.querySelector('#mini-cart') ||
      document.querySelector('cart-drawer.mini-cart') ||
      document.querySelector('.mini-cart.drawer') ||
      document.querySelector('.drawer--large');

    const opener =
      document.querySelector('[aria-controls="mini-cart"]') ||
      document.querySelector('button[aria-controls*="cart"]') ||
      document.querySelector('a[href="/cart"]');

    return !!(
      (opener && opener.getAttribute('aria-expanded') === 'true') ||
      document.body.classList.contains('lock-all') ||
      document.body.classList.contains('el-cart-open') ||
      document.body.classList.contains('cart-drawer-open') ||
      (
        drawer &&
        getComputedStyle(drawer).visibility !== 'hidden' &&
        getComputedStyle(drawer).display !== 'none' &&
        getComputedStyle(drawer).transform !== 'matrix(1, 0, 0, 1, 500, 0)'
      )
    );
  } catch (_) {
    return false;
  }
}

function elOpenMiniCartAfterAdd() {
  /*
    Primeira abertura do drawer às vezes precisa de mais de uma tentativa,
    porque o tema ainda está renderizando/atualizando o mini-cart.
  */
  [160, 420, 850, 1400].forEach(function (delay) {
    setTimeout(function () {
      try {
        if (!elIsMiniCartOpen()) {
          openMiniCartByAria();
        }
      } catch (_) {}
    }, delay);
  });
}

async function refreshCartUI(openCart, addResult) {
  try {
    const cart = await getCart();

    if (addResult && addResult.sections) {
      cart.sections = addResult.sections;
    }

    updateCartCounters(cart);

    /*
      Dispara os eventos nativos que o tema escuta.
      Isso ajuda o mini-cart/drawer/header a ficarem sincronizados.
    */
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cart,
          openMiniCart: openCart ? true : false,
          replacementDelay: 0
        }
      }));
    } catch (_) {}

    /*
      Fallback visual: se o tema não re-renderizar sozinho, mantemos nosso mini-cart básico.
    */
    setTimeout(function () {
      updateCartCounters(cart);
    }, 120);

    setTimeout(function () {
      updateCartCounters(cart);
    }, 500);

    setTimeout(function () {
      updateCartCounters(cart);
    }, 1200);

    if (!cart.sections) {
      updateMiniCartBasic(cart);
    }

    window.dispatchEvent(new CustomEvent('el:cart:updated', { detail: cart }));
    document.dispatchEvent(new CustomEvent('el:cart:updated', { detail: cart }));

    if (openCart) {
  elOpenMiniCartAfterAdd();
}

    return cart;
  } catch (error) {
    console.warn('[Espírito Livre] Não foi possível atualizar o carrinho:', error);
    return null;
  }
}

  async function addToCart(form) {
    if (!form || isQuickBuyLocked()) throw new Error('locked_or_missing_form');
    if (!hasVariantId(form)) throw new Error('missing_variant_id');

    const formData = new FormData(form);

    if (!formData.get('quantity')) {
      formData.set('quantity', '1');
    }

if (!formData.get('sections')) {
  formData.append('sections', 'mini-cart');
}

    const res = await fetch(API.add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    let data = null;

    try {
      data = await res.json();
    } catch (_) {}

    if (!res.ok) {
      const message = data && data.description ? data.description : 'add_to_cart_failed';
      throw new Error(message);
    }

    return data;
  }

  /* =========================================================
     BIND DOS FORMULÁRIOS DE PRODUTO
  ========================================================= */

  function bindProductForms() {
    qsa(SELECTORS.productForm).forEach((form) => {
      if (!getProductForm(form)) return;
      if (form.__elSafeBound) return;

      form.__elSafeBound = true;

form.addEventListener('submit', async function (event) {
  if (
    elIsSearchContext(event.target) ||
    elIsSearchContext(event.submitter) ||
    elIsSearchContext(document.activeElement)
  ) {
    try {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    } catch (_) {}

    elSubmitSearchFromContext(document.activeElement || event.target);
    return false;
  }

  const submitBtn = getSubmitButton(form, event);

        if (isCheckoutAction(submitBtn) || isCheckoutAction(event.target)) {
          return;
        }

        if (isQuickBuyLocked()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }

        const realForm = getProductForm(form);

        if (!realForm) return;

        event.preventDefault();
        event.stopPropagation();

        if (isAdding || form.__elAdding) return false;

        isAdding = true;
        form.__elAdding = true;

        const oldText = submitBtn ? submitBtn.textContent : '';

        try {
          if (submitBtn) {
            submitBtn.setAttribute('disabled', 'disabled');
            submitBtn.classList.add('el-is-adding');

            const label = submitBtn.querySelector('.el-label') || submitBtn;
            if (label && label.textContent) label.textContent = 'ADICIONANDO...';
          }

          const img = qs(SELECTORS.productImage) || qs('img');
          const cartIcon = findCartIcon();

const addResult = await addToCart(realForm);

          await flyToCartAnimation(img, cartIcon);

          showAddedBadge(cartIcon);

          if (cartIcon && cartIcon.getBoundingClientRect) {
            const r = cartIcon.getBoundingClientRect();
            launchConfettiLight(r.left + r.width / 2, r.top + r.height / 2);
          }

await refreshCartUI(true, addResult);
        } catch (error) {
          console.warn('[Espírito Livre] Erro ao adicionar ao carrinho:', error);

          try {
            form.submit();
          } catch (_) {}
        } finally {
          isAdding = false;
          form.__elAdding = false;

          if (submitBtn) {
            submitBtn.removeAttribute('disabled');
            submitBtn.classList.remove('el-is-adding');

            const label = submitBtn.querySelector('.el-label') || submitBtn;
            if (label && oldText) label.textContent = oldText;
          }
        }

        return false;
      }, true);
    });
  }

  /* =========================================================
     FALLBACK CIRÚRGICO: CLIQUE DIRETO NO BOTÃO DO PDP
     - Se o botão visual não disparar submit nativo, este bloco assume.
     - Não atua dentro do Quick Buy.
     - Não atua em checkout.
     - Usa o mesmo fluxo oficial do custom.js: addToCart + fly + badge + mini-cart.
  ========================================================= */

function installBuyButtonClickFallback() {
  if (document.__elBuyButtonClickFallbackInstalled) return;
  document.__elBuyButtonClickFallbackInstalled = true;

document.addEventListener('click', async function (event) {
  if (elIsSearchContext(event.target) || elIsSearchContext(document.activeElement)) {
    return;
  }

  const btn = safeClosest(event.target, SELECTORS.addToCartBtn);

    if (!btn) return;
    if (isCheckoutAction(btn) || isCheckoutAction(event.target)) return;

    /*
      Proteção absoluta:
      busca, predictive search e formulários /search nunca podem virar add-to-cart.
    */
    const searchContext = safeClosest(btn, [
      'form[action*="/search"]',
      'form[role="search"]',
      'form.search',
      'form.predictive-search',
      'predictive-search',
      '.predictive-search',
      '.search',
      '[role="search"]'
    ].join(','));

    if (searchContext) {
      return;
    }

    if (
      safeClosest(btn, '#qbsSheet') ||
      safeClosest(btn, '#qbsOverlay') ||
      safeClosest(btn, 'quick-buy-drawer') ||
      safeClosest(btn, 'quick-buy-popover')
    ) {
      return;
    }

    /*
      Ponto mais importante:
      agora o fallback SÓ age se o botão clicado estiver ligado
      a um formulário real de produto.
      Removemos o fallback perigoso para document.querySelector('form[action*="/cart/add"]').
    */
    const directForm =
      btn.form ||
      safeClosest(btn, 'form[action*="/cart/add"], .product-form');

    const form = getProductForm(directForm || btn);

    if (!form) return;

    try {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    } catch (_) {}

    if (isQuickBuyLocked()) {
      return false;
    }

    if (isAdding || form.__elAdding) {
      return false;
    }

    isAdding = true;
    form.__elAdding = true;

    try {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('el-is-adding');

      const img = qs(SELECTORS.productImage) || qs('img');
      const cartIcon = findCartIcon();

      const addResult = await addToCart(form);

      await flyToCartAnimation(img, cartIcon);

      showAddedBadge(cartIcon);

      if (cartIcon && cartIcon.getBoundingClientRect) {
        const r = cartIcon.getBoundingClientRect();
        launchConfettiLight(r.left + r.width / 2, r.top + r.height / 2);
      }

      await refreshCartUI(true, addResult);
    } catch (error) {
      console.warn('[Espírito Livre] Fallback do botão não conseguiu adicionar:', error);

      try {
        form.submit();
      } catch (_) {}
    } finally {
      isAdding = false;
      form.__elAdding = false;

      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
      btn.classList.remove('el-is-adding');
    }

    return false;
  }, true);
}

  /* =========================================================
     PROTEÇÃO: QUICK BUY ABERTO NÃO PODE ACIONAR PDP POR BAIXO
  ========================================================= */

  document.addEventListener('submit', function (event) {
    if (!isQuickBuyLocked()) return;
    if (isCheckoutAction(event.target)) return;

    const form = getProductForm(event.target);

    if (form) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);

  document.addEventListener('click', function (event) {
    if (!isQuickBuyLocked()) return;
    if (isCheckoutAction(event.target)) return;

    const action = safeClosest(event.target, [
      SELECTORS.addToCartBtn,
      'form[action*="/cart/add"] button',
      'form[action*="/cart/add"] input[type="submit"]'
    ].join(','));

    if (action) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);

  /* =========================================================
     STICKY BUY MOBILE
  ========================================================= */

  function initStickyBuy() {
    const stickyEl = document.getElementById('el-sticky-buy');
    const btn = document.getElementById('elStickyGo');

    if (!stickyEl || !btn) return;

    function measureSticky() {
      if (!isMobile()) {
        stickyEl.style.display = 'none';
        return;
      }

      stickyEl.style.display = '';

      const h = Math.round(stickyEl.getBoundingClientRect().height || 84);
      document.documentElement.style.setProperty('--el-sticky-h', h + 2 + 'px');
    }

    function setAddMode(on) {
      btn.dataset.mode = on ? 'add' : '';

      const label = btn.querySelector('.el-label') || btn;

      if (label) {
        label.textContent = on ? 'ADICIONAR AO CARRINHO' : '✦ COMPRAR AGORA';
      }
    }

    function openQuickBuySheet() {
      if (typeof window.qbsOpen === 'function') {
        try {
          window.qbsOpen();
          setAddMode(true);
          return true;
        } catch (_) {}
      }

      try {
        window.dispatchEvent(new Event('qbs:open'));
        setAddMode(true);
        return true;
      } catch (_) {}

      const sheet = document.getElementById('qbsSheet');
      const overlay = document.getElementById('qbsOverlay');

      if (sheet && overlay) {
        overlay.classList.add('open');
        sheet.classList.add('open');
        sheet.setAttribute('aria-hidden', 'false');
        window.__EL_QUICK_BUY_LOCK = true;
        setAddMode(true);

        try {
          window.dispatchEvent(new Event('qbs:opened'));
        } catch (_) {}

        return true;
      }

      return false;
    }

    window.addEventListener('resize', measureSticky, { passive: true });
    window.addEventListener('qbs:opened', () => setAddMode(true), { passive: true });
    window.addEventListener('qbs:open', () => setAddMode(true), { passive: true });
    window.addEventListener('qbs:closed', () => setAddMode(false), { passive: true });

    btn.addEventListener('click', function (event) {
      if (!isMobile()) return;

      event.preventDefault();
      event.stopPropagation();

      if (btn.dataset.mode === 'add') {
        try {
          window.dispatchEvent(new Event('qbs:add'));
        } catch (_) {}

        return;
      }

      openQuickBuySheet();
    }, true);

    measureSticky();
  }

  /* =========================================================
     CSS
  ========================================================= */

  function injectStyles() {
    const id = 'el-custom-js-style';

    if (document.getElementById(id)) return;

    const css = `
      .el-added-badge {
        position: fixed;
        z-index: 2147483646;
        padding: 8px 12px;
        border-radius: 999px;
        background: linear-gradient(90deg, #23d07f, #06c2d7);
        color: #02121a;
        font-weight: 900;
        font-size: 12px;
        letter-spacing: .04em;
        box-shadow: 0 8px 26px rgba(0, 0, 0, .35);
        opacity: 0;
        transform: translateY(4px);
        transition: opacity .28s ease, transform .28s ease;
        pointer-events: none;
      }

      .el-added-badge.is-visible {
        opacity: 1;
        transform: translateY(-4px);
      }

      .el-confetti-root {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 2147483645;
        overflow: hidden;
      }

      .el-confetti-piece {
        position: absolute;
        opacity: .95;
        transform: translate3d(0, 0, 0) rotate(0deg);
        transition: transform 950ms cubic-bezier(.2,.9,.2,1), opacity 850ms linear;
      }

      .el-is-adding {
        opacity: .72;
        cursor: progress !important;
      }

      .el-mini-cart-item {
        display: flex;
        gap: 10px;
        align-items: center;
        padding: 10px 0;
      }

      .el-mini-cart-thumb img {
        width: 64px;
        height: 64px;
        object-fit: cover;
        border-radius: 8px;
      }

      .el-mini-cart-title {
        font-weight: 700;
        line-height: 1.2;
      }

      .el-mini-cart-variant,
      .el-mini-cart-qty,
      .el-mini-cart-price {
        font-size: 12px;
        opacity: .82;
      }
    `;

    const style = document.createElement('style');
    style.id = id;
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  /* =========================================================
     INIT
  ========================================================= */

function init() {
  injectStyles();
installSearchSubmitGuard();
bindProductForms();
installBuyButtonClickFallback();
installCartMutationSync();
initStickyBuy();
refreshCartUI(false);

document.addEventListener('shopify:section:load', function () {
  bindProductForms();
  installBuyButtonClickFallback();
  initStickyBuy();
  refreshCartUI(false);
});

    document.addEventListener('shopify:section:select', function () {
      bindProductForms();
    });

    window.EL_BIND_PRODUCT_FORMS = bindProductForms;
    window.EL_REFRESH_CART = refreshCartUI;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (!observerStarted) {
    observerStarted = true;

    let bindTimer = null;

    const observer = new MutationObserver(() => {
      clearTimeout(bindTimer);
      bindTimer = setTimeout(bindProductForms, 180);
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
async function elSyncCartFromServer() {
  try {
    const cart = await getCart();

    updateCartCounters(cart);

    window.dispatchEvent(new CustomEvent('el:cart:updated', { detail: cart }));
    document.dispatchEvent(new CustomEvent('el:cart:updated', { detail: cart }));

    return cart;
  } catch (_) {
    return null;
  }
}

function elScheduleCartSync() {
  [180, 600, 1200, 2000].forEach(function (delay) {
    setTimeout(elSyncCartFromServer, delay);
  });
}

function elIsCartMutationUrl(url) {
  url = String(url || '');

  return (
    url.indexOf('/cart/change') !== -1 ||
    url.indexOf('/cart/update') !== -1 ||
    url.indexOf('/cart/clear') !== -1 ||
    url.indexOf('/cart/add') !== -1
  );
}

function installCartMutationSync() {
  if (window.__EL_CART_MUTATION_SYNC_INSTALLED__) return;
  window.__EL_CART_MUTATION_SYNC_INSTALLED__ = true;

  /*
    Captura cliques de remover/alterar quantidade dentro do mini-cart.
  */
  document.addEventListener('click', function (event) {
    const target = event.target;

    if (!target || !target.closest) return;

    const insideCart =
      target.closest('#mini-cart') ||
      target.closest('cart-drawer') ||
      target.closest('.mini-cart') ||
      target.closest('.cart-drawer');

    if (!insideCart) return;

    const mutationButton = target.closest(
      'a[href*="/cart/change"], ' +
      'button[name="minus"], ' +
      'button[name="plus"], ' +
      'button[name="remove"], ' +
      '[data-line-item-remove], ' +
      '[data-action="remove"], ' +
      '[data-action="decrease-quantity"], ' +
      '[data-action="increase-quantity"], ' +
      '.line-item__remove-button, ' +
      '.quantity-selector__button'
    );

    if (mutationButton) {
      elScheduleCartSync();
    }
  }, true);

  document.addEventListener('change', function (event) {
    const target = event.target;

    if (!target || !target.closest) return;

    const insideCart =
      target.closest('#mini-cart') ||
      target.closest('cart-drawer') ||
      target.closest('.mini-cart') ||
      target.closest('.cart-drawer');

    if (!insideCart) return;

    if (
      target.matches('input[name="updates[]"]') ||
      target.matches('input[name*="quantity"]') ||
      target.matches('select[name*="quantity"]')
    ) {
      elScheduleCartSync();
    }
  }, true);

  /*
    Observador leve: não altera fetch/XHR, só escuta quando o tema mexe no carrinho.
  */
  try {
    if (window.fetch && !window.fetch.__elCartMutationSyncWrapped) {
      const originalFetch = window.fetch.bind(window);

      window.fetch = function (input, init) {
        const url =
          typeof input === 'string'
            ? input
            : input && input.url
              ? input.url
              : '';

        return originalFetch(input, init).then(function (response) {
          if (elIsCartMutationUrl(url)) {
            elScheduleCartSync();
          }

          return response;
        });
      };

      window.fetch.__elCartMutationSyncWrapped = true;
    }
  } catch (_) {}

  try {
    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__elCartMutationSyncWrapped) {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          this.__elCartMutationSyncUrl = url || '';
        } catch (_) {}

        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        try {
          const xhr = this;

          xhr.addEventListener('loadend', function () {
            if (elIsCartMutationUrl(xhr.__elCartMutationSyncUrl || '')) {
              elScheduleCartSync();
            }
          });
        } catch (_) {}

        return originalSend.apply(this, arguments);
      };

      XMLHttpRequest.prototype.__elCartMutationSyncWrapped = true;
    }
  } catch (_) {}

  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      updateCartCounters(event.detail.cart);
    } else {
      elScheduleCartSync();
    }
  });

  document.addEventListener('cart:refresh', elScheduleCartSync);
}

})();